/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL ICONS — DS v2
 * ══════════════════════════════════════════════════════════════════════════════
 * 
 * Biblioteca centralizada de iconos SVG para PTEL.
 * Migrada desde ptel-icons.tsx (v1) → DS v2.
 * 
 * Uso:
 *   import { Icons } from '@/design-system/v2';
 *   <Icons.Search size={16} />
 * 
 * ══════════════════════════════════════════════════════════════════════════════
 */

import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface IconProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  strokeWidth?: number;
}

type IconComponent = React.FC<IconProps>;

// ─────────────────────────────────────────────────────────────────────────────
// HELPER
// ─────────────────────────────────────────────────────────────────────────────

const createIcon = (
  paths: React.ReactNode,
  defaultSize: number = 24,
  defaultStrokeWidth: number = 2
): IconComponent => {
  return ({ size = defaultSize, className, style, strokeWidth = defaultStrokeWidth }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      {paths}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONOS
// ─────────────────────────────────────────────────────────────────────────────

export const Icons = {
  // ─── LOGO ───────────────────────────────────────────────────────────────────
  MapLogo: ({ size = 32, className, style }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} style={style}>
      <path d="M6 8L12 6L20 10L26 8V24L20 26L12 22L6 24V8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      <path d="M12 6V22" stroke="currentColor" strokeWidth="2"/>
      <path d="M20 10V26" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),

  // ─── NAVEGACIÓN Y ACCIONES ──────────────────────────────────────────────────
  Search: createIcon(
    <>
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.3-4.3"/>
    </>,
    18
  ),

  Plus: createIcon(
    <>
      <path d="M5 12h14"/>
      <path d="M12 5v14"/>
    </>,
    18
  ),

  X: createIcon(
    <>
      <path d="M18 6 6 18"/>
      <path d="m6 6 12 12"/>
    </>,
    18
  ),

  XSmall: createIcon(
    <>
      <path d="M18 6 6 18"/>
      <path d="m6 6 12 12"/>
    </>,
    14, 2.5
  ),

  XTiny: createIcon(
    <>
      <path d="M18 6 6 18"/>
      <path d="m6 6 12 12"/>
    </>,
    10, 2.5
  ),

  Check: createIcon(
    <polyline points="20 6 9 17 4 12"/>,
    14, 3
  ),

  CheckCircle: createIcon(
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </>,
    16
  ),

  // ─── CHEVRONS ───────────────────────────────────────────────────────────────
  ChevronDown: createIcon(
    <path d="m6 9 6 6 6-6"/>,
    16
  ),

  ChevronDownSmall: createIcon(
    <path d="m6 9 6 6 6-6"/>,
    14
  ),

  ChevronUp: createIcon(
    <path d="m18 15-6-6-6 6"/>,
    16
  ),

  ChevronRight: createIcon(
    <path d="m9 18 6-6-6-6"/>,
    16
  ),

  ChevronLeft: createIcon(
    <path d="m15 18-6-6 6-6"/>,
    16
  ),

  // ─── FLECHAS ────────────────────────────────────────────────────────────────
  ArrowRight: createIcon(
    <>
      <path d="M5 12h14"/>
      <path d="m12 5 7 7-7 7"/>
    </>,
    14
  ),

  ArrowRightSmall: createIcon(
    <>
      <path d="M5 12h14"/>
      <path d="m12 5 7 7-7 7"/>
    </>,
    10, 2.5
  ),

  ArrowLeft: createIcon(
    <>
      <path d="M19 12H5"/>
      <path d="m12 19-7-7 7-7"/>
    </>,
    14
  ),

  // ─── CATEGORÍAS TEXTLEARNING ────────────────────────────────────────────────
  Shield: createIcon(
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
  ),

  FileText: createIcon(
    <>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
      <polyline points="14 2 14 8 20 8"/>
    </>
  ),

  Book: createIcon(
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
  ),

  Edit: createIcon(
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </>
  ),

  Sparkles: createIcon(
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  ),

  // ─── FUENTES DE DATOS ───────────────────────────────────────────────────────
  Globe: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
      <path d="M2 12h20"/>
    </>,
    12
  ),

  Brain: createIcon(
    <>
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </>,
    12
  ),

  // ─── UI ELEMENTOS ───────────────────────────────────────────────────────────
  Info: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 16v-4"/>
      <path d="M12 8h.01"/>
    </>,
    18
  ),

  Lock: createIcon(
    <>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </>,
    16
  ),

  Mail: createIcon(
    <>
      <rect width="20" height="16" x="2" y="4" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </>,
    18
  ),

  Clock: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </>,
    16
  ),

  // ─── TEMA ───────────────────────────────────────────────────────────────────
  Sun: createIcon(
    <>
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2"/>
      <path d="M12 20v2"/>
      <path d="m4.93 4.93 1.41 1.41"/>
      <path d="m17.66 17.66 1.41 1.41"/>
      <path d="M2 12h2"/>
      <path d="M20 12h2"/>
      <path d="m6.34 17.66-1.41 1.41"/>
      <path d="m19.07 4.93-1.41 1.41"/>
    </>,
    20
  ),

  Moon: createIcon(
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>,
    20
  ),

  // ─── ARCHIVOS Y DATOS ───────────────────────────────────────────────────────
  FileCode: createIcon(
    <>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
      <polyline points="14 2 14 8 20 8"/>
    </>,
    14
  ),

  Download: createIcon(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" x2="12" y1="15" y2="3"/>
    </>,
    16
  ),

  Upload: createIcon(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" x2="12" y1="3" y2="15"/>
    </>,
    16
  ),

  History: createIcon(
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
      <path d="M12 7v5l4 2"/>
    </>,
    16
  ),

  // ─── LAYOUT ─────────────────────────────────────────────────────────────────
  LayoutGrid: createIcon(
    <>
      <rect width="7" height="7" x="3" y="3" rx="1"/>
      <rect width="7" height="7" x="14" y="3" rx="1"/>
      <rect width="7" height="7" x="14" y="14" rx="1"/>
      <rect width="7" height="7" x="3" y="14" rx="1"/>
    </>,
    16
  ),

  List: createIcon(
    <>
      <line x1="8" x2="21" y1="6" y2="6"/>
      <line x1="8" x2="21" y1="12" y2="12"/>
      <line x1="8" x2="21" y1="18" y2="18"/>
      <line x1="3" x2="3.01" y1="6" y2="6"/>
      <line x1="3" x2="3.01" y1="12" y2="12"/>
      <line x1="3" x2="3.01" y1="18" y2="18"/>
    </>,
    16
  ),

  // ─── INDICADORES ────────────────────────────────────────────────────────────
  Activity: createIcon(
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>,
    14
  ),

  Filter: createIcon(
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>,
    12
  ),

  Hash: createIcon(
    <>
      <line x1="4" y1="9" x2="20" y2="9"/>
      <line x1="4" y1="15" x2="20" y2="15"/>
      <line x1="10" y1="3" x2="8" y2="21"/>
      <line x1="16" y1="3" x2="14" y2="21"/>
    </>,
    12, 2.5
  ),

  Layers: createIcon(
    <>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/>
      <path d="m22 12.65-8.58 4.35a2 2 0 0 1-1.66 0L2 12.65"/>
      <path d="m22 17.65-8.58 4.35a2 2 0 0 1-1.66 0L2 17.65"/>
    </>,
    12
  ),

  // ─── COORDENADAS / MAPAS ────────────────────────────────────────────────────
  MapPin: createIcon(
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </>,
    16
  ),

  Compass: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </>,
    16
  ),

  Target: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </>,
    16
  ),

  Expand: createIcon(
    <>
      <path d="M15 3h6v6"/>
      <path d="M9 21H3v-6"/>
      <path d="M21 3l-7 7"/>
      <path d="M3 21l7-7"/>
    </>,
    16
  ),

  Maximize: createIcon(
    <>
      <path d="M8 3H5a2 2 0 0 0-2 2v3"/>
      <path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
      <path d="M3 16v3a2 2 0 0 0 2 2h3"/>
      <path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
    </>,
    16
  ),

  // ─── PROCESO ────────────────────────────────────────────────────────────────
  Play: createIcon(
    <polygon points="5 3 19 12 5 21 5 3"/>,
    16
  ),

  Pause: createIcon(
    <>
      <rect width="4" height="16" x="6" y="4"/>
      <rect width="4" height="16" x="14" y="4"/>
    </>,
    16
  ),

  RotateCw: createIcon(
    <>
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
    </>,
    16
  ),

  RefreshCw: createIcon(
    <>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M8 16H3v5"/>
    </>,
    16
  ),

  // ─── ALERTAS ────────────────────────────────────────────────────────────────
  AlertTriangle: createIcon(
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
      <path d="M12 9v4"/>
      <path d="M12 17h.01"/>
    </>,
    16
  ),

  AlertCircle: createIcon(
    <>
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" x2="12" y1="8" y2="12"/>
      <line x1="12" x2="12.01" y1="16" y2="16"/>
    </>,
    16
  ),

  // ─── VALIDACIÓN ─────────────────────────────────────────────────────────────
  CheckSquare: createIcon(
    <>
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </>,
    16
  ),

  XSquare: createIcon(
    <>
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
      <line x1="9" x2="15" y1="9" y2="15"/>
      <line x1="15" x2="9" y1="9" y2="15"/>
    </>,
    16
  ),

  // ─── CONFIGURACIÓN ──────────────────────────────────────────────────────────
  Settings: createIcon(
    <>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
      <circle cx="12" cy="12" r="3"/>
    </>,
    16
  ),

  Sliders: createIcon(
    <>
      <line x1="4" x2="4" y1="21" y2="14"/>
      <line x1="4" x2="4" y1="10" y2="3"/>
      <line x1="12" x2="12" y1="21" y2="12"/>
      <line x1="12" x2="12" y1="8" y2="3"/>
      <line x1="20" x2="20" y1="21" y2="16"/>
      <line x1="20" x2="20" y1="12" y2="3"/>
      <line x1="2" x2="6" y1="14" y2="14"/>
      <line x1="10" x2="14" y1="8" y2="8"/>
      <line x1="18" x2="22" y1="16" y2="16"/>
    </>,
    16
  ),

  // ─── USUARIOS ───────────────────────────────────────────────────────────────
  User: createIcon(
    <>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </>,
    16
  ),

  Users: createIcon(
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </>,
    16
  ),

  // ─── EXTERNOS ───────────────────────────────────────────────────────────────
  ExternalLink: createIcon(
    <>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" x2="21" y1="14" y2="3"/>
    </>,
    14
  ),

  Copy: createIcon(
    <>
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
    </>,
    14
  ),

  Trash: createIcon(
    <>
      <path d="M3 6h18"/>
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
    </>,
    16
  ),

  // ─── ICONOS ADICIONALES v43 ─────────────────────────────────────────────────
  Loader: createIcon(
    <>
      <line x1="12" x2="12" y1="2" y2="6"/>
      <line x1="12" x2="12" y1="18" y2="22"/>
      <line x1="4.93" x2="7.76" y1="4.93" y2="7.76"/>
      <line x1="16.24" x2="19.07" y1="16.24" y2="19.07"/>
      <line x1="2" x2="6" y1="12" y2="12"/>
      <line x1="18" x2="22" y1="12" y2="12"/>
      <line x1="4.93" x2="7.76" y1="19.07" y2="16.24"/>
      <line x1="16.24" x2="19.07" y1="7.76" y2="4.93"/>
    </>,
    16
  ),

  ShieldX: createIcon(
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <line x1="9" x2="15" y1="9" y2="15"/>
      <line x1="15" x2="9" y1="9" y2="15"/>
    </>,
    16
  ),

  Zap: createIcon(
    <>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </>,
    16
  ),

  BookOpen: createIcon(
    <>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </>,
    16
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT
// ─────────────────────────────────────────────────────────────────────────────

export default Icons;
